import { Injectable, inject, Injector, runInInjectionContext, isDevMode } from '@angular/core';
import { ADMIN_UID } from 'src/app/config/admin.config';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  deleteDoc,
  serverTimestamp,
  orderBy,
  docData,
  collectionData,
  increment,
  collectionGroup,
  Timestamp,
  runTransaction,
  updateDoc, // ✅ (MIN) para update best-effort fuera de TX
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { map, Observable, Subject } from 'rxjs';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { limit } from 'firebase/firestore';

// ✅ NUEVO: Analytics (GA4 / Firebase Analytics)
import { getAnalytics, logEvent } from 'firebase/analytics';

// ⬇️ NUEVO: servicio de insignias (racha 5/10/30, etc.)
import { BadgesService } from 'src/app/services/badges.service';
// ⬇️ NUEVO: compresor de imagen
import { ImageCompressorService } from 'src/app/services/image-compressor.service';

export type PathKey = 'favorites' | 'surroundings' | 'selfies';
export type SequenceKey = 'base' | PathKey;

export interface Challenge {
  id: string;
  title: string;
  description: string;
  category: string;
  type: 'photo' | 'video' | 'text' | string;

  /**
   * ✅ day = SIEMPRE el número que se muestra al usuario.
   * - base: 1..20
   * - paths: 1..12
   */
  day: number;

  /**
   * ✅ intervalId = el ID real usado para Firestore/Explore/Feed.
   * - base: 1..20
   * - favorites: 101..112
   * - surroundings: 201..212
   * - selfies: 301..312
   */
  intervalId?: number;

  /**
   * Para que Today pueda colorear / mostrar pathLabel sin deducirlo fuera
   */
  sequenceKey?: SequenceKey;

  completed?: boolean;
  status?: 'completed' | 'missed' | 'active' | 'locked';
}

// ⬇️ NUEVO: tipos de reacción (impulsos)
export type ReactionType = 'creative' | 'brave' | 'goodVibes';

@Injectable({ providedIn: 'root' })
export class ChallengeService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private storage = inject(Storage);
  private injector = inject(Injector);
  private badgesService = inject(BadgesService);
  private imageCompressor = inject(ImageCompressorService);

  // ✅ Persistencia de intervalo activo (para recarga PWA sin perder contexto)
  private readonly ACTIVE_INTERVAL_KEY = 'challup_active_interval';

  // ✅ Compresión “rápida / mínimo aceptable” para feed
  private readonly IMG_MAX_W = 1080;
  private readonly IMG_MAX_H = 1080;
  private readonly IMG_QUALITY = 0.55;

  // ✅ NUEVO: objetivo de peso aprox (para que cargue bien incluso con mala cobertura)
  // Ajusta aquí si quieres todavía más ligero: 280–320 KB
  private readonly IMG_TARGET_KB = 350;
  private readonly IMG_MIN_QUALITY = 0.45;

  // Helper: ejecuta llamadas AngularFire dentro del injection context
  private inCtx<T>(fn: () => Promise<T>): Promise<T> {
    return runInInjectionContext(this.injector, fn);
  }

  // ========= ✅ ANALYTICS (GA4) =========
  private analyticsErrorCode(err: any): string {
    return err?.code || err?.name || (typeof err === 'string' ? err : 'unknown');
  }

  private logAnalytics(eventName: string, params: Record<string, any> = {}): void {
    try {
      if (typeof window === 'undefined') return;

      const analytics = getAnalytics();
      // debug_mode => para que salga en DebugView (solo en dev)
      const debug = isDevMode();

      const payload: Record<string, any> = { ...params };
      // ✅ FIX TS4111: acceso por index signature
      if (debug) payload['debug_mode'] = true;

      logEvent(analytics, eventName, payload);
    } catch {
      // Si analytics no está disponible / bloqueado, no rompemos nada
    }
  }

  /**
   * ⚠️ Por compat: antes se llamaba activeChallengeId y se entendía como "day".
   * Ahora lo tratamos como "intervalId" real (1..20, 101.., 201.., 301..).
   */
  private activeChallengeId: number | null = null;

  // 🔹 Caché en memoria del reto actual (para evitar flashes al volver a Today)
  private cachedCurrentChallenge: Challenge | null = null;

  private forceFirstChallenge = false;
  private readonly TEN_MIN = 10 * 60 * 1000;
  private readonly CHALLUP_START = new Date('2025-10-20T18:00:00Z');
  private readonly CHALLENGE_INTERVAL_MS = 10 * 60 * 1000;

  // 🔹 Tamaño de la secuencia (para pantalla fin de viaje)
  // 👉 ahora lo usamos como TOTAL de retos de la secuencia base
  private readonly TOTAL_SEQUENCE_DAYS = 20;

  // ✅ FASE 2: caminos (intervalos)
  private readonly PATH_LENGTH = 12;

  // ✅ cola por (user + submission) para evitar carreras por clicks rápidos
  private reactionQueue = new Map<string, Promise<void>>();

  constructor() {
    // ✅ Recuperar intervalo activo guardado (PWA reload)
    try {
      if (typeof window !== 'undefined') {
        const saved = Number(localStorage.getItem(this.ACTIVE_INTERVAL_KEY));
        if (Number.isFinite(saved) && saved > 0) {
          this.activeChallengeId = saved;
        }
      }
    } catch {}
  }

  private getPathMetaFromInterval(
    interval: number
  ): { key: PathKey; index: number; total: number } | null {
    if (!Number.isFinite(interval)) return null;

    // 101..112 => favorites
    if (interval >= 101 && interval <= 100 + this.PATH_LENGTH) {
      return { key: 'favorites', index: interval - 100, total: this.PATH_LENGTH };
    }

    // 201..212 => surroundings
    if (interval >= 201 && interval <= 200 + this.PATH_LENGTH) {
      return { key: 'surroundings', index: interval - 200, total: this.PATH_LENGTH };
    }

    // 301..312 => selfies
    if (interval >= 301 && interval <= 300 + this.PATH_LENGTH) {
      return { key: 'selfies', index: interval - 300, total: this.PATH_LENGTH };
    }

    return null;
  }

  private getPathLabel(key: PathKey): string {
    if (key === 'favorites') return 'Favoritos';
    if (key === 'surroundings') return 'Mi Entorno';
    return 'Selfies';
  }

  private getPathStart(key: PathKey): number {
    if (key === 'favorites') return 101;
    if (key === 'surroundings') return 201;
    return 301;
  }

  private getIntervalForPathDay(key: PathKey, index: number): number {
    const start = this.getPathStart(key);
    const safeIndex = Math.max(1, Math.min(index, this.PATH_LENGTH));
    return start + (safeIndex - 1);
  }

  /**
   * Devuelve meta de display para cualquier interval:
   * - base: day=interval (1..20), total=20
   * - path: day=1..12, total=12, key=path
   */
  private getDisplayMeta(interval: number): {
    sequenceKey: SequenceKey;
    displayDay: number;
    displayTotal: number;
    pathKey?: PathKey;
    pathLabel?: string;
  } {
    const meta = this.getPathMetaFromInterval(interval);
    if (meta) {
      return {
        sequenceKey: meta.key,
        displayDay: meta.index,
        displayTotal: meta.total,
        pathKey: meta.key,
        pathLabel: this.getPathLabel(meta.key),
      };
    }
    return {
      sequenceKey: 'base',
      displayDay: interval,
      displayTotal: this.TOTAL_SEQUENCE_DAYS,
    };
  }

  private getFirstIncompletePathInterval(completed: number[], key: PathKey): number {
    const start = this.getPathStart(key);
    for (let i = 0; i < this.PATH_LENGTH; i++) {
      const interval = start + i;
      if (!completed.includes(interval)) return interval;
    }
    // si está todo hecho, devuelve el último (para quedarse ahí)
    return start + (this.PATH_LENGTH - 1);
  }

  // 👇 ADMIN bypass (necesario para canAccessFeed y vistas)
  private static readonly ADMIN_UID = ADMIN_UID;

  // 🔔 Ping para Tabs cuando el usuario sube el reto del día
  public submissionMade$ = new Subject<void>();

  // ✅ Sync UI Explore/Feed cuando cambia una reacción (en esta misma sesión)
  public reactionChanged$ = new Subject<{
    interval: number;
    submissionId: string;
    userUid: string;
    ownerUid: string | null;
    beforeType: ReactionType | null;
    afterType: ReactionType | null;
    deltaTotal: number; // -1/0/+1
    action: 'add' | 'remove' | 'switch';
  }>();

  // ========= UTILIDADES BÁSICAS =========

  enableResetMode() {
    this.forceFirstChallenge = true;
  }

  /**
   * ⚠️ Compat: recibe interval real
   */
  setActiveChallengeId(interval: number) {
    this.activeChallengeId = interval;

    // ✅ persistencia para recargas PWA
    try {
      if (typeof window !== 'undefined' && Number.isFinite(interval) && interval > 0) {
        localStorage.setItem(this.ACTIVE_INTERVAL_KEY, String(interval));
      }
    } catch {}
  }

  getActiveChallengeId(): number | null {
    return this.activeChallengeId;
  }

  private getSpainNow(): Date {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  }

  private async ensureAuth(): Promise<User> {
    const u = this.auth.currentUser;
    if (u) return u;
    return new Promise((resolve, reject) => {
      const unsub = onAuthStateChanged(
        this.auth,
        (usr) => {
          if (usr) {
            unsub();
            resolve(usr);
          }
        },
        (err) => {
          unsub();
          reject(err);
        }
      );
    });
  }

  private extractPathFromUrl(url?: string | null): string | null {
    if (!url) return null;
    try {
      const decoded = decodeURIComponent(url);
      const match = decoded.match(/\/o\/(.*?)\?alt=media/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  /**
   * Helper central: borra el fichero de Storage asociado a un doc de participación.
   * Prioriza `storagePath`, pero si no existe, lo intenta extraer de `mediaUrl` o `url`.
   */
  private async deleteMediaFromData(data: any): Promise<void> {
    if (!data) return;

    const storagePath =
      data.storagePath ??
      this.extractPathFromUrl(data.mediaUrl) ??
      this.extractPathFromUrl(data.url);

    if (!storagePath) return;

    try {
      await deleteObject(ref(this.storage, storagePath));
    } catch {
      // Si ya no existe el fichero no pasa nada
    }
  }

  // Helper rápido
  private isAdminCurrent(): boolean {
    const uid = this.auth.currentUser?.uid;
    return !!uid && uid === ChallengeService.ADMIN_UID;
  }

  /**
   * ✅ NUEVO: extensión por mime (solo para coherencia si algún día subes original por fallback)
   */
  private getExtFromMime(mime?: string | null): string {
    const m = String(mime || '').toLowerCase();
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
    if (m.includes('png')) return 'png';
    if (m.includes('webp')) return 'webp';
    if (m.includes('gif')) return 'gif';
    return 'jpg';
  }

  /**
   * ✅ NUEVO: compresión robusta orientada a “carga rápida en mala cobertura”.
   * - Intenta varias pasadas (calidad / tamaño) hasta acercarse a IMG_TARGET_KB
   * - Si algo falla, sube original (como antes)
   *
   * ✅ MIN FIX: si la “mejor” opción NO reduce tamaño, NO la usamos (nunca subimos más pesado que el original).
   */
  private async compressForUploadBestEffort(input: Blob): Promise<{
    blob: Blob;
    compressed: boolean;
    usedW: number;
    usedH: number;
    usedQ: number;
  }> {
    const originalSize = input.size;
    const targetBytes = this.IMG_TARGET_KB * 1024;

    // Intentos ordenados: primero mantenemos tamaño, luego bajamos calidad, luego bajamos tamaño.
    const attempts: Array<{ w: number; h: number; q: number }> = [
      { w: this.IMG_MAX_W, h: this.IMG_MAX_H, q: this.IMG_QUALITY },
      { w: this.IMG_MAX_W, h: this.IMG_MAX_H, q: Math.max(this.IMG_MIN_QUALITY, this.IMG_QUALITY - 0.08) },
      { w: this.IMG_MAX_W, h: this.IMG_MAX_H, q: Math.max(this.IMG_MIN_QUALITY, this.IMG_QUALITY - 0.15) },
      { w: 960, h: 960, q: Math.max(this.IMG_MIN_QUALITY, this.IMG_QUALITY) },
      { w: 900, h: 900, q: Math.max(this.IMG_MIN_QUALITY, this.IMG_QUALITY - 0.05) },
      { w: 720, h: 720, q: Math.max(this.IMG_MIN_QUALITY, this.IMG_QUALITY) },
      { w: 720, h: 720, q: this.IMG_MIN_QUALITY },
    ];

    let best: { blob: Blob; w: number; h: number; q: number } | null = null;

    for (const a of attempts) {
      try {
        const b = await this.imageCompressor.compress(input, a.w, a.h, a.q);

        // ✅ Si no reduce bytes, no lo usamos como “best”
        if (b.size < originalSize) {
          const candidate = { blob: b, w: a.w, h: a.h, q: a.q };
          if (!best || candidate.blob.size < best.blob.size) best = candidate;

          // Si ya estamos por debajo del objetivo, paramos
          if (b.size <= targetBytes) {
            return {
              blob: b,
              compressed: true,
              usedW: a.w,
              usedH: a.h,
              usedQ: a.q,
            };
          }
        }
      } catch {
        // seguimos probando
      }
    }

    // ✅ Si hay best, seguro reduce respecto al original
    if (best) {
      return {
        blob: best.blob,
        compressed: true,
        usedW: best.w,
        usedH: best.h,
        usedQ: best.q,
      };
    }

    // fallback total (o ya era pequeño / compresión no mejora)
    return {
      blob: input,
      compressed: false,
      usedW: this.IMG_MAX_W,
      usedH: this.IMG_MAX_H,
      usedQ: this.IMG_QUALITY,
    };
  }

  /**
   * Devuelve SIEMPRE el primer día NO completado en base a completedIntervals.
   * SOLO mira base 1..20.
   */
  private getFirstIncompleteDay(completed: number[]): number {
    const normalized = Array.isArray(completed)
      ? [...new Set(completed.filter((n) => Number.isFinite(n) && n > 0))]
      : [];

    for (let d = 1; d <= this.TOTAL_SEQUENCE_DAYS; d++) {
      if (!normalized.includes(d)) {
        return d;
      }
    }
    return this.TOTAL_SEQUENCE_DAYS;
  }

  // ========= NUEVOS MÉTODOS PARA FEED / DÍA ACTUAL PERSONAL =========

  async getCurrentDaySafe(): Promise<number> {
    try {
      const user = this.auth.currentUser ?? (await this.ensureAuth());
      if (user) {
        const st = await this.ensureUserState(user.uid, true);
        if (st?.currentDay && Number.isFinite(st.currentDay)) return st.currentDay;
      }
    } catch (err) {
      console.error('getCurrentDaySafe error:', err);
    }

    return 1;
  }

  async userHasSubmissionForDay(uid: string, day: number): Promise<boolean> {
    if (!uid || !Number.isFinite(day)) return false;

    try {
      const flatRef = collection(this.firestore, 'submissions');
      const qFlat = query(
        flatRef,
        where('uid', '==', uid),
        where('day', '==', day),
        where('status', '==', 'approved')
      );
      const flatSnap = await getDocs(qFlat);
      if (!flatSnap.empty) return true;
    } catch {}

    return this.hasAnySubmissionFor(uid, day);
  }

  // ========= ESTADO DEL USUARIO =========

  /**
   * Estado base + fase2:
   * - currentDay: derivado SIEMPRE de base (1..20)
   * - completedIntervals: contiene base y paths (1..20, 101.., 201.., 301..)
   * - selectedPath: camino elegido (o null)
   */
  async ensureUserState(uid: string, readonly = false) {
    const ref = doc(this.firestore, `userProgress/${uid}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      const now = this.getSpainNow();
      const initial = {
        currentDay: 1,
        startedAt: now.toISOString(),
        completedIntervals: [] as number[],
        missedDays: [] as number[],
        streak: 0,
        coins: 0,
        lastCompletedInterval: null,
        lastCompletedAt: null,
        impulsesReceived: 0,
        impulsesGiven: 0,
        selectedPath: null as PathKey | null,
        updatedAt: serverTimestamp(),
      };
      if (!readonly) {
        await setDoc(ref, initial);
      }
      return initial;
    }

    const d: any = snap.data();

    const completed = Array.isArray(d?.completedIntervals) ? d.completedIntervals : [];

    // 🔹 currentDay BASE siempre se deriva de 1..20
    const derivedCurrentDay = this.getFirstIncompleteDay(completed);

    const missedDays = Array.isArray(d?.missedDays) ? d.missedDays : [];

    // selectedPath (fase 2)
    const selectedPath: PathKey | null =
      d?.selectedPath === 'favorites' ||
      d?.selectedPath === 'surroundings' ||
      d?.selectedPath === 'selfies'
        ? d.selectedPath
        : null;

    // Alinear currentDay guardado solo si escribimos
    if (!readonly && d?.currentDay !== derivedCurrentDay) {
      await this.setUserState(uid, { currentDay: derivedCurrentDay }, d);
    }

    return {
      currentDay: derivedCurrentDay,
      startedAt: d?.startedAt ?? null,
      completedIntervals: completed,
      missedDays,
      streak: d?.streak ?? 0,
      coins: d?.coins ?? 0,
      lastCompletedInterval: d?.lastCompletedInterval ?? null,
      lastCompletedAt: d?.lastCompletedAt ?? null,
      impulsesReceived: d?.impulsesReceived ?? 0,
      impulsesGiven: d?.impulsesGiven ?? 0,
      hasUnlockedPionero: d?.hasUnlockedPionero ?? false,
      selectedPath,
    };
  }

  async setSelectedPath(uid: string, path: PathKey): Promise<void> {
    if (!uid) return;
    const patch = { selectedPath: path, updatedAt: serverTimestamp() };
    await setDoc(doc(this.firestore, `userProgress/${uid}`), patch, { merge: true });
  }

  // Legacy: antes devolvía el reto global por tiempo; ahora devolvemos 1 como valor neutro.
  public getCurrentGlobalChallengeNumber(): number {
    return 1;
  }

  public getWindowDurationMs(): number {
    return this.CHALLENGE_INTERVAL_MS;
  }

  async updateUserMissedChallenges(uid: string, currentDay: number) {
    const ref = doc(this.firestore, `userProgress/${uid}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const data: any = snap.data();
    const completed: number[] = data.completedIntervals || [];
    const prevMissed: number[] = data.missedDays || [];

    const missedDays = [...prevMissed];

    for (let i = 1; i < currentDay; i++) {
      if (!completed.includes(i) && !missedDays.includes(i)) {
        missedDays.push(i);
      }
    }

    await setDoc(ref, { missedDays, updatedAt: serverTimestamp() }, { merge: true });
  }

  private async setUserState(uid: string, patch: any, currentData?: any) {
    const ref = doc(this.firestore, `userProgress/${uid}`);

    let before: any = currentData;
    if (!before) {
      const snap = await getDoc(ref);
      before = snap.exists() ? snap.data() : {};
    }

    let changed = false;
    for (const [key, value] of Object.entries(patch)) {
      if ((before as any)[key] !== value) {
        changed = true;
        break;
      }
    }

    if (!changed) return;

    await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  }

  // ========= SUBIDAS / PARTICIPACIÓN =========

  async uploadUserMediaDataUrl(interval: number, dataUrl: string): Promise<string> {
    const user = await this.ensureAuth();

    const t0 = Date.now();
    this.logAnalytics('challenge_submit_start', {
      interval,
      source: 'dataUrl',
    });

    try {
      // ⬇️ comprimimos la imagen antes de subir (robusto + objetivo de peso)
      const originalBlob = await (await fetch(dataUrl)).blob();
      const originalKb = Math.round(originalBlob.size / 1024);

      let blobToUpload: Blob = originalBlob;
      let compressed = false;
      let usedW = this.IMG_MAX_W;
      let usedH = this.IMG_MAX_H;
      let usedQ = this.IMG_QUALITY;

      try {
        const res = await this.compressForUploadBestEffort(originalBlob);
        blobToUpload = res.blob;
        compressed = res.compressed;
        usedW = res.usedW;
        usedH = res.usedH;
        usedQ = res.usedQ;
      } catch (e) {
        console.warn('⚠️ Compresión falló, subiendo original:', e);
        blobToUpload = originalBlob;
        compressed = false;
      }

      const uploadKb = Math.round(blobToUpload.size / 1024);

      const mime = blobToUpload.type || 'image/jpeg';
      const ext = this.getExtFromMime(mime);
      const fileName = `${Date.now()}.${ext}`;

      const path = `user_uploads/${user.uid}/${interval}/${fileName}`;
      const fileRef = ref(this.storage, path);

      await uploadBytes(fileRef, blobToUpload, {
        contentType: mime,
        cacheControl: 'public,max-age=31536000,immutable',
        customMetadata: { userId: user.uid },
      });

      const url = await getDownloadURL(fileRef);

      this.logAnalytics('challenge_submit_success', {
        interval,
        source: 'dataUrl',
        duration_ms: Date.now() - t0,
        original_kb: originalKb,
        upload_kb: uploadKb,
        compressed,
        used_w: usedW,
        used_h: usedH,
        used_q: usedQ,
      });

      return url;
    } catch (err) {
      this.logAnalytics('challenge_submit_fail', {
        interval,
        source: 'dataUrl',
        duration_ms: Date.now() - t0,
        error_code: this.analyticsErrorCode(err),
      });
      throw err;
    }
  }

  // 🔧 ARREGLADO: usamos SIEMPRE el perfil del usuario (/users) para nombre + avatar
  async saveSubmission(interval: number, mediaUrl: string, storagePath?: string): Promise<void> {
    const user = await this.ensureAuth();
    const feedDocId = `${user.uid}_${interval}`;

    let userName: string = user.displayName || 'Usuario ChallUp';
    let userAvatar: string = user.photoURL || 'assets/default-avatar.png';

    try {
      const userRef = doc(this.firestore, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const u = userSnap.data() as any;

        userName = u?.displayName ?? u?.userName ?? u?.name ?? userName;

        userAvatar =
          u?.avatar ??
          u?.photoURL ??
          u?.PhotoURL ??
          u?.photoUrl ??
          u?.photo ??
          userAvatar;
      }
    } catch (e) {
      console.warn('⚠️ No se pudo leer el perfil de usuario para la submission:', e);
    }

    const baseReactions = {
      creative: 0,
      brave: 0,
      goodVibes: 0,
      total: 0,
    };

    const finalStoragePath = storagePath ?? this.extractPathFromUrl(mediaUrl);

    const subRef = doc(this.firestore, `challengesSequence/${interval}/submissions/${feedDocId}`);
    await setDoc(subRef, {
      userId: user.uid,
      userName,
      userAvatar,
      mediaUrl,
      timestamp: serverTimestamp(),
      interval,
      likes: 0,
      status: 'approved',
      reactions: baseReactions,
      // ✅ NUEVO: guardar path (más fiable para delete)
      storagePath: finalStoragePath ?? null,
    });

    const userSubRef = doc(this.firestore, `userSubmissions/${feedDocId}`);
    await setDoc(userSubRef, {
      userId: user.uid,
      userName,
      userAvatar,
      mediaUrl,
      interval,
      timestamp: serverTimestamp(),
      // ✅ NUEVO: preferimos el path que ya sabemos
      storagePath: finalStoragePath ?? null,
      reactions: baseReactions,
      // opcional, pero útil
      likes: 0,
    });
  }

  async submitSubmission(challengeId: string, file: File): Promise<void> {
    const user = await this.ensureAuth();

    const interval = Number(challengeId);
    const t0 = Date.now();

    const originalKb = file?.size ? Math.round(file.size / 1024) : undefined;

    this.logAnalytics('challenge_submit_start', {
      interval: Number.isFinite(interval) ? interval : undefined,
      source: 'file',
      original_kb: originalKb,
    });

    try {
      // ⬇️ comprimimos la imagen antes de subir (robusto + objetivo de peso)
      let blobToUpload: Blob = file;
      let compressed = false;
      let usedW = this.IMG_MAX_W;
      let usedH = this.IMG_MAX_H;
      let usedQ = this.IMG_QUALITY;

      try {
        const res = await this.compressForUploadBestEffort(file);
        blobToUpload = res.blob;
        compressed = res.compressed;
        usedW = res.usedW;
        usedH = res.usedH;
        usedQ = res.usedQ;
      } catch (e) {
        console.warn('⚠️ Compresión falló, subiendo original:', e);
        blobToUpload = file;
        compressed = false;
      }

      const uploadKb = Math.round(blobToUpload.size / 1024);

      const mime = blobToUpload.type || 'image/jpeg';
      const ext = this.getExtFromMime(mime);
      const fileName = `${Date.now()}.${ext}`;

      const path = `user_uploads/${user.uid}/${challengeId}/${fileName}`;
      const fileRef = ref(this.storage, path);

      await uploadBytes(fileRef, blobToUpload, {
        contentType: mime,
        cacheControl: 'public,max-age=31536000,immutable',
        customMetadata: { userId: user.uid },
      });

      const mediaUrl = await getDownloadURL(fileRef);

      await this.saveSubmission(Number(challengeId), mediaUrl, path);
      await this.completeCurrentChallenge();

      this.logAnalytics('challenge_submit_success', {
        interval: Number.isFinite(interval) ? interval : undefined,
        source: 'file',
        duration_ms: Date.now() - t0,
        original_kb: originalKb,
        upload_kb: uploadKb,
        compressed,
        used_w: usedW,
        used_h: usedH,
        used_q: usedQ,
      });
    } catch (err) {
      this.logAnalytics('challenge_submit_fail', {
        interval: Number.isFinite(interval) ? interval : undefined,
        source: 'file',
        duration_ms: Date.now() - t0,
        error_code: this.analyticsErrorCode(err),
      });
      throw err;
    }
  }

  /**
   * ✅ Completa el intervalo activo (base o path).
   * - activeChallengeId se interpreta como interval real
   * - currentDay guardado se mantiene como "base day" derivado
   * - localStorage de celebration usa displayDay (no 101)
   */
  async completeCurrentChallenge(): Promise<void> {
    const user = await this.ensureAuth();
    const refProg = doc(this.firestore, `userProgress/${user.uid}`);
    const snap = await getDoc(refProg);
    const rawData: any = snap.exists() ? snap.data() : await this.ensureUserState(user.uid);

    const completed: number[] = Array.isArray(rawData?.completedIntervals)
      ? rawData.completedIntervals
      : [];

    const derivedBaseDay = this.getFirstIncompleteDay(completed);

    let intervalToComplete = this.activeChallengeId ?? derivedBaseDay;

    if (!Number.isFinite(intervalToComplete) || intervalToComplete <= 0) {
      intervalToComplete = derivedBaseDay;
    }

    const alreadyCompleted = completed.includes(intervalToComplete);

    let addCoins = 0;
    let newStreak = rawData?.streak || 0;
    let unlockPioneroNow = false;

    let lastCompletedAt: Date | null = rawData?.lastCompletedAt
      ? (rawData.lastCompletedAt as Timestamp).toDate()
      : null;

    const now = this.getSpainNow();

    if (!alreadyCompleted) {
      completed.push(intervalToComplete);
      addCoins = 10;

      const MAX_GAP_MS = 24 * 60 * 60 * 1000;
      const nowMs = now.getTime();
      const lastMs = lastCompletedAt ? lastCompletedAt.getTime() : null;

      if (!lastMs) {
        newStreak = 1;
      } else {
        const diff = nowMs - lastMs;
        newStreak = diff <= MAX_GAP_MS ? (rawData?.streak || 0) + 1 : 1;
      }

      lastCompletedAt = now;

      if (!rawData?.hasUnlockedPionero) {
        unlockPioneroNow = true;
      }
    }

    const newCoins = (rawData?.coins || 0) + addCoins;

    // El "nextDay" base se deriva SOLO de 1..20, ignore paths
    const nextDayBase = this.getFirstIncompleteDay(completed);

    const patch: any = {
      completedIntervals: completed,
      coins: newCoins,
      updatedAt: serverTimestamp(),
    };

    if (!alreadyCompleted) {
      patch.streak = newStreak;
      patch.lastCompletedInterval = intervalToComplete;
      patch.lastCompletedAt = lastCompletedAt;
      patch.currentDay = nextDayBase;
      if (unlockPioneroNow) patch.hasUnlockedPionero = true;
    }

    await setDoc(refProg, patch, { merge: true });

    // ✅ LocalStorage para Celebration (display, NO intervalId)
    const meta = this.getDisplayMeta(intervalToComplete);
    const completedCountNow = completed.length;

    try {
      localStorage.setItem('justCompletedChallenge', '1');
      localStorage.setItem('justCompletedChallenge_day', String(meta.displayDay));
      localStorage.setItem('justCompletedChallenge_totalDays', String(meta.displayTotal));
      localStorage.setItem('justCompletedChallenge_sequence', meta.sequenceKey);
      if (meta.pathKey) localStorage.setItem('justCompletedChallenge_path', meta.pathKey);
      if (meta.pathLabel) localStorage.setItem('justCompletedChallenge_pathLabel', meta.pathLabel);

      // conservamos tus claves existentes
      localStorage.setItem('justCompletedChallenge_completedCount', String(completedCountNow));
      localStorage.setItem(
        'justCompletedChallenge_totalDays_base',
        String(this.TOTAL_SEQUENCE_DAYS)
      );
    } catch {}

    // ✅ Badges por completados (1/5/10/20)
    if (!alreadyCompleted) {
      try {
        await this.badgesService.checkUnlocks(completedCountNow, user.uid);
      } catch (err) {
        console.error('checkUnlocks (completados) error:', err);
      }
    }

    if (unlockPioneroNow) {
      try {
        await this.awardPioneroIfFirstTime(user.uid);
      } catch (err) {
        console.error('awardPioneroIfFirstTime error:', err);
      }
      try {
        localStorage.setItem('challup_pionero_unlocked', '1');
      } catch {}
    }

    // active = intervalo real (para Explore/Feed)
    this.setActiveChallengeId(intervalToComplete);

    // cache: marcar completed por intervalId
    if (
      this.cachedCurrentChallenge &&
      (this.cachedCurrentChallenge.intervalId ?? this.cachedCurrentChallenge.day) === intervalToComplete
    ) {
      this.cachedCurrentChallenge = {
        ...this.cachedCurrentChallenge,
        completed: true,
      };
    }

    window.dispatchEvent(
      new CustomEvent('challup:stats-updated', {
        detail: { coins: newCoins, streak: newStreak },
      })
    );

    this.submissionMade$.next();

    // 🔔 Push solo base (como tenías)
    if (!alreadyCompleted) {
      if (intervalToComplete <= this.TOTAL_SEQUENCE_DAYS) {
        try {
          await this.announceUserChallengeChangeFor(user.uid, nextDayBase);
        } catch (err) {
          console.error('announceUserChallengeChangeFor error:', err);
        }
      }
    }
  }

  // ⛔️ Reset TOTAL de PARTICIPACIÓN
  async deleteAllUserData(uid: string): Promise<void> {
    console.log('♻️ Iniciando borrado completo de PARTICIPACIÓN del usuario:', uid);
    try {
      try {
        sessionStorage.removeItem('explore_sticky_active');
        sessionStorage.removeItem('explore_sticky_day');
        localStorage.removeItem('forceShowFeed');
        localStorage.removeItem('feedAccess');
        localStorage.removeItem('justCompletedChallenge');
        localStorage.removeItem('justCompletedChallenge_day');
        localStorage.removeItem('justCompletedChallenge_expiry');
        localStorage.removeItem('justCompletedChallenge_completedCount');
        localStorage.removeItem('justCompletedChallenge_totalDays');
        localStorage.removeItem('justCompletedChallenge_sequence');
        localStorage.removeItem('justCompletedChallenge_path');
        localStorage.removeItem('justCompletedChallenge_pathLabel');
        localStorage.removeItem('justCompletedChallenge_totalDays_base');
        localStorage.removeItem('challup_pionero_unlocked');
        localStorage.removeItem('profile_removed_status');
        localStorage.removeItem('profile_removed_intervals');

        // ✅ NUEVO: limpiar intervalo persistido
        localStorage.removeItem(this.ACTIVE_INTERVAL_KEY);
      } catch {}

      const baseIntervals = Array.from({ length: this.TOTAL_SEQUENCE_DAYS }, (_, i) => i + 1);
      const favIntervals = Array.from({ length: this.PATH_LENGTH }, (_, i) => 101 + i);
      const surIntervals = Array.from({ length: this.PATH_LENGTH }, (_, i) => 201 + i);
      const selIntervals = Array.from({ length: this.PATH_LENGTH }, (_, i) => 301 + i);

      const intervals = [...baseIntervals, ...favIntervals, ...surIntervals, ...selIntervals];

      for (const interval of intervals) {
        const flatId = `${uid}_${interval}`;

        // 0.1 userSubmissions/{uid_interval}
        try {
          const usRef = doc(this.firestore, `userSubmissions/${flatId}`);
          const usSnap = await getDoc(usRef);
          if (usSnap.exists()) {
            const d = usSnap.data() as any;
            await this.deleteMediaFromData(d);
            await deleteDoc(usRef);
          }
        } catch {}

        // 0.2 challengesSequence/{interval}/submissions/{uid_interval}
        const parentIds = [String(interval), `day_${interval}`];
        for (const parentId of parentIds) {
          try {
            const subRef = doc(
              this.firestore,
              `challengesSequence/${parentId}/submissions/${flatId}`
            );
            const subSnap = await getDoc(subRef);
            if (subSnap.exists()) {
              const sdata = subSnap.data() as any;

              try {
                const reactionsRef = collection(
                  this.firestore,
                  `challengesSequence/${parentId}/submissions/${flatId}/reactions`
                );
                const reactionsSnap = await getDocs(reactionsRef);
                for (const r of reactionsSnap.docs) {
                  try {
                    await deleteDoc(r.ref);
                  } catch {}
                }
              } catch {}

              await this.deleteMediaFromData(sdata);
              await deleteDoc(subRef);
            }
          } catch {}
        }
      }

      // 🔹 1) Borrar todas las userSubmissions del usuario (+ fotos)
      try {
        const subsRef = collection(this.firestore, 'userSubmissions');

        const snapByUserId = await getDocs(query(subsRef, where('userId', '==', uid)));
        const snapByUid = await getDocs(query(subsRef, where('uid', '==', uid)));

        const processedUserSubs = new Set<string>();
        const allUserSubDocs = [...snapByUserId.docs, ...snapByUid.docs];

        for (const d of allUserSubDocs) {
          if (processedUserSubs.has(d.id)) continue;
          processedUserSubs.add(d.id);

          const data = d.data() as any;
          await this.deleteMediaFromData(data);

          try {
            await deleteDoc(doc(this.firestore, `userSubmissions/${d.id}`));
          } catch {}
        }
      } catch {}

      // 🔹 2) Borrar submissions del usuario dentro de cada reto
      try {
        const challengesRef = collection(this.firestore, 'challengesSequence');
        const challengesSnap = await getDocs(challengesRef);

        for (const ch of challengesSnap.docs) {
          const challengeId = ch.id;
          const subsCollRef = collection(this.firestore, `challengesSequence/${challengeId}/submissions`);

          const snapSubsByUserId = await getDocs(query(subsCollRef, where('userId', '==', uid)));
          const snapSubsByUid = await getDocs(query(subsCollRef, where('uid', '==', uid)));

          const processedSubmissions = new Set<string>();
          const allSubDocs = [...snapSubsByUserId.docs, ...snapSubsByUid.docs];

          for (const s of allSubDocs) {
            if (processedSubmissions.has(s.id)) continue;
            processedSubmissions.add(s.id);

            const sdata = s.data() as any;

            try {
              const reactionsRef = collection(
                this.firestore,
                `challengesSequence/${challengeId}/submissions/${s.id}/reactions`
              );
              const reactionsSnap = await getDocs(reactionsRef);
              for (const r of reactionsSnap.docs) {
                try {
                  await deleteDoc(r.ref);
                } catch {}
              }
            } catch (e) {
              console.warn('⚠️ No se pudieron borrar reacciones de submission', s.id, e);
            }

            await this.deleteMediaFromData(sdata);

            try {
              await deleteDoc(doc(this.firestore, `challengesSequence/${challengeId}/submissions/${s.id}`));
            } catch {}
          }

          const feedDocId = `${uid}_${challengeId}`;
          if (!processedSubmissions.has(feedDocId)) {
            try {
              try {
                const reactionsRef = collection(
                  this.firestore,
                  `challengesSequence/${challengeId}/submissions/${feedDocId}/reactions`
                );
                const reactionsSnap = await getDocs(reactionsRef);
                for (const r of reactionsSnap.docs) {
                  try {
                    await deleteDoc(r.ref);
                  } catch {}
                }
              } catch {}

              const extraRef = doc(this.firestore, `challengesSequence/${challengeId}/submissions/${feedDocId}`);
              const extraSnap = await getDoc(extraRef);
              if (extraSnap.exists()) {
                const extraData = extraSnap.data() as any;
                await this.deleteMediaFromData(extraData);
                await deleteDoc(extraRef);
              }
            } catch {}
          }
        }
      } catch (e) {
        console.warn('⚠️ Error limpiando challengesSequence en reset:', e);
      }

      // 🔹 3) Borrar colección PLANA "submissions" (legacy)
      try {
        const flatRef = collection(this.firestore, 'submissions');

        const fSnapByUserId = await getDocs(query(flatRef, where('userId', '==', uid)));
        const fSnapByUid = await getDocs(query(flatRef, where('uid', '==', uid)));

        const processedFlat = new Set<string>();
        const flatDocs = [...fSnapByUserId.docs, ...fSnapByUid.docs];

        for (const f of flatDocs) {
          if (processedFlat.has(f.id)) continue;
          processedFlat.add(f.id);

          const fdata = f.data() as any;
          await this.deleteMediaFromData(fdata);

          try {
            await deleteDoc(f.ref);
          } catch {}
        }
      } catch (e) {
        console.warn('⚠️ No se pudieron limpiar docs legacy de "submissions":', e);
      }

      // 🔹 4) Borrar TODAS las reacciones que este usuario haya hecho a fotos de otros
      try {
        const reactionsCg = collectionGroup(this.firestore, 'reactions');
        const qByUser = query(reactionsCg, where('userId', '==', uid));
        const reactionsSnap = await getDocs(qByUser);
        for (const r of reactionsSnap.docs) {
          try {
            await deleteDoc(r.ref);
          } catch {}
        }
        console.log('🧹 Reacciones del usuario eliminadas de otros feeds:', reactionsSnap.size);
      } catch (e) {
        console.warn('⚠️ No se pudieron limpiar reacciones del usuario en collectionGroup:', e);
      }

      // 🔹 5) LEGADO: limpiar "participations"
      try {
        const partsRef = collection(this.firestore, 'participations');

        const pSnapByUserId = await getDocs(query(partsRef, where('userId', '==', uid)));
        const pSnapByUid = await getDocs(query(partsRef, where('uid', '==', uid)));

        const processedParts = new Set<string>();
        const allPartsDocs = [...pSnapByUserId.docs, ...pSnapByUid.docs];

        for (const p of allPartsDocs) {
          if (processedParts.has(p.id)) continue;
          processedParts.add(p.id);

          try {
            await deleteDoc(p.ref);
          } catch {}
        }
      } catch {}

      // 🔹 6) NUEVO: limpiar marcas de eliminado en userChallenges/{uid}/daily
      try {
        const dailyRef = collection(this.firestore, `userChallenges/${uid}/daily`);
        const dailySnap = await getDocs(dailyRef);

        for (const docSnap of dailySnap.docs) {
          try {
            await deleteDoc(docSnap.ref);
          } catch {}
        }
        console.log('🧹 Limpieza de userChallenges/daily completada para', uid);
      } catch (e) {
        console.warn('⚠️ No se pudo limpiar userChallenges/daily en reset:', e);
      }

      // 🔹 7) Resetear progreso
      const nowDate = this.getSpainNow();
      const baseState = {
        currentDay: 1,
        startedAt: nowDate.toISOString(),
        completedIntervals: [] as number[],
        missedDays: [] as number[],
        streak: 0,
        coins: 0,
        lastCompletedInterval: null,
        lastCompletedAt: null,
        impulsesReceived: 0,
        impulsesGiven: 0,
        selectedPath: null as PathKey | null,
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(this.firestore, `userProgress/${uid}`), baseState, { merge: true });

      this.cachedCurrentChallenge = null;
      this.activeChallengeId = null;

      this.enableResetMode();
      console.log('✅ Reset de participación completado.');

      this.submissionMade$.next();
    } catch (err) {
      console.error('❌ Error al resetear participación del usuario:', err);
      throw err;
    }
  }

  // ========= CHALLENGES =========

  /**
   * Devuelve el reto que toca HOY según:
   * - base no completada => siguiente base
   * - base completada + selectedPath => siguiente del camino
   * - base completada + sin selectedPath => se queda en el último base (para que Today muestre selector)
   */
  async getChallengeForCurrentInterval(): Promise<Challenge | null> {
    const user = await this.ensureAuth();

    const state = await this.ensureUserState(user.uid, true);
    const completed: number[] = Array.isArray(state.completedIntervals) ? state.completedIntervals : [];

    const baseFirstIncomplete = this.getFirstIncompleteDay(completed);
    const baseAllDone = Array.from({ length: this.TOTAL_SEQUENCE_DAYS }, (_, i) => i + 1).every(
      (d) => completed.includes(d)
    );

    let intervalToShow: number;

    if (!baseAllDone) {
      intervalToShow = baseFirstIncomplete; // 1..20
    } else if (state.selectedPath) {
      intervalToShow = this.getFirstIncompletePathInterval(completed, state.selectedPath); // 101.. etc
    } else {
      // Base terminado y aún no eligió camino => mantener último base
      intervalToShow = this.TOTAL_SEQUENCE_DAYS;
    }

    // cache por intervalId
    const cachedInterval = this.cachedCurrentChallenge
      ? (this.cachedCurrentChallenge.intervalId ?? this.cachedCurrentChallenge.day)
      : null;

    if (this.cachedCurrentChallenge && cachedInterval === intervalToShow) {
      this.cachedCurrentChallenge.completed = completed.includes(intervalToShow);
      this.setActiveChallengeId(intervalToShow);
      return this.cachedCurrentChallenge;
    }

    const ch = await this.getChallengeByDay(intervalToShow);
    if (!ch) {
      this.cachedCurrentChallenge = null;
      return null;
    }

    ch.completed = completed.includes(intervalToShow);

    this.setActiveChallengeId(intervalToShow);
    this.cachedCurrentChallenge = ch;

    return ch;
  }

  /**
   * ⚠️ Firma conservada: le pasas "day", pero realmente es "interval".
   * Devuelve Challenge con:
   * - day = displayDay (1..20 / 1..12)
   * - intervalId = interval real
   */
  async getChallengeByDay(day: number): Promise<Challenge | null> {
    try {
      const interval = day;

      let refDoc = doc(this.firestore, `challengesSequence/day_${interval}`);
      let snap = await getDoc(refDoc);
      if (!snap.exists()) {
        refDoc = doc(this.firestore, `challengesSequence/${interval}`);
        snap = await getDoc(refDoc);
      }

      const meta = this.getDisplayMeta(interval);

      if (!snap.exists()) {
        // ✅ Fallback: si es un path, NO mostramos "101"
        if (meta.sequenceKey !== 'base') {
          return {
            id: `day_${interval}`,
            title: `Reto ${meta.displayDay}/${meta.displayTotal}`,
            description: `Reto de camino (placeholder). Añade el contenido real en challengesSequence/day_${interval}`,
            category: 'photo',
            type: 'photo',
            day: meta.displayDay,
            intervalId: interval,
            sequenceKey: meta.sequenceKey,
            completed: false,
          };
        }

        // base fallback
        return {
          id: `day_${interval}`,
          title: `Reto ${interval}`,
          description: 'Desafíate a ti mismo y comparte tu resultado',
          category: 'photo',
          type: 'photo',
          day: interval,
          intervalId: interval,
          sequenceKey: 'base',
          completed: false,
        };
      }

      const data = snap.data() as any;

      // ✅ Si es path, day visible = index (1..12), intervalId = 101.. etc
      // ✅ Si es base, day visible = interval (1..20)
      return {
        id: `day_${interval}`,
        title:
          data.title ||
          (meta.sequenceKey !== 'base'
            ? `Reto ${meta.displayDay}/${meta.displayTotal}`
            : `Reto ${interval}`),
        description: data.description || 'Desafíate a ti mismo y comparte tu resultado',
        category: data.category || 'photo',
        type: data.type || 'photo',
        day: meta.displayDay,
        intervalId: interval,
        sequenceKey: meta.sequenceKey,
        completed: false,
      };
    } catch (err) {
      console.error('Error obteniendo challenge por día:', err);
      return null;
    }
  }

  // ========= ACCESO FEED =========

  async hasAnySubmissionFor(uid: string, interval: number): Promise<boolean> {
    const flatId = `${uid}_${interval}`;
    const flatRef = doc(this.firestore, `userSubmissions/${flatId}`);
    const flatSnap = await getDoc(flatRef);
    if (flatSnap.exists()) return true;

    const nestedRef = doc(this.firestore, `challengesSequence/${interval}/submissions/${flatId}`);
    const nestedSnap = await getDoc(nestedRef);
    return nestedSnap.exists();
  }

  async canAccessFeed(uid: string, interval: number): Promise<boolean> {
    if (!uid || interval == null) return false;
    if (uid === ChallengeService.ADMIN_UID) return true;

    try {
      const subCol = collection(this.firestore, `challengesSequence/${interval}/submissions`);
      const qSubs = query(subCol, where('userId', '==', uid), limit(1));
      const snap = await getDocs(qSubs);
      return !snap.empty;
    } catch {
      return false;
    }
  }

  // ========= HISTÓRICO / FEED =========

  async getUserChallengeHistory(
    uid: string
  ): Promise<
    {
      day: number;
      completed: boolean;
      status: 'completed' | 'missed' | 'active' | 'locked';
      url?: string | null;
    }[]
  > {
    const state = await this.ensureUserState(uid, true);
    const completed = state.completedIntervals || [];
    const current = state.currentDay || 1;
    const missedDays = state.missedDays || [];

    const userSubsRef = collection(this.firestore, 'userSubmissions');
    const qSubs = query(userSubsRef, where('userId', '==', uid));
    const subsSnap = await getDocs(qSubs);
    const urlByInterval = new Map<number, string>();
    subsSnap.docs.forEach((docu) => {
      const d: any = docu.data();
      if (d?.['interval'] && d?.['mediaUrl']) urlByInterval.set(d['interval'], d['mediaUrl']);
    });

    const maxDay = Math.min(Math.max(current, 1), this.TOTAL_SEQUENCE_DAYS);
    const history = [];

    for (let i = 1; i <= this.TOTAL_SEQUENCE_DAYS; i++) {
      let status: 'completed' | 'missed' | 'active' | 'locked' = 'locked';
      if (completed.includes(i)) status = 'completed';
      else if (missedDays.includes(i)) status = 'missed';
      else if (i === maxDay) status = 'active';
      else status = 'locked';

      history.push({
        day: i,
        completed: completed.includes(i),
        status,
        url: urlByInterval.get(i) || null,
      });
    }

    return history;
  }

  getUserGallery(userId: string): Observable<any[]> {
    const userSubsRef = collection(this.firestore, 'userSubmissions');
    const qUser = query(userSubsRef, where('userId', '==', userId), orderBy('interval', 'asc'));

    return collectionData(qUser, { idField: 'id' }).pipe(
      map((items: any[]) => items.filter((item: any) => item?.status !== 'removed'))
    );
  }

  getParticipationsByUserId(userId: string): Observable<any[]> {
    return this.getUserGallery(userId);
  }

  async getFeedForInterval(
    interval: number
  ): Promise<
    Array<{
      id: string;
      userId: string;
      mediaUrl: string;
      timestamp: any;
      interval: number;
      likes: number;
      status: string;
      userName?: string;
      userAvatar?: string;
      reactions?: {
        creative: number;
        brave: number;
        goodVibes: number;
        total: number;
      };
    }>
  > {
    const subsRef = collection(this.firestore, `challengesSequence/${interval}/submissions`);

    const qSubs = query(subsRef, orderBy('timestamp', 'desc'));
    const snap = await getDocs(qSubs);
    const all = snap.docs.map((d) => {
      const data = d.data() as any;
      const reactions = data?.reactions ?? {
        creative: 0,
        brave: 0,
        goodVibes: 0,
        total: 0,
      };
      return {
        id: d.id,
        ...data,
        likes: data?.likes ?? reactions.total ?? 0,
        status: (data?.status ?? 'approved') as string,
        reactions,
      };
    });

    if (this.isAdminCurrent()) return all;

    return all.filter((it) => it.status === 'approved' || (it as any).status == null);
  }

  async getAllGalleriesForInterval(
    interval: number
  ): Promise<
    Array<{
      id: string;
      userId: string;
      mediaUrl: string;
      timestamp: any;
      interval: number;
      likes?: number;
      status: string;
      userName?: string;
      userAvatar?: string;
      reactions?: {
        creative: number;
        brave: number;
        goodVibes: number;
        total: number;
      };
    }>
  > {
    const subsRef = collection(this.firestore, `challengesSequence/${interval}/submissions`);
    const qAll = query(subsRef, orderBy('timestamp', 'desc'));
    const snap = await getDocs(qAll);
    return snap.docs.map((d) => {
      const data = d.data() as any;
      const reactions = data?.reactions ?? {
        creative: 0,
        brave: 0,
        goodVibes: 0,
        total: 0,
      };
      return {
        id: d.id,
        ...data,
        likes: data?.likes ?? reactions.total ?? 0,
        status: (data?.status ?? 'approved') as string,
        reactions,
      };
    });
  }

  async getMySubmissionForInterval(
    interval: number
  ): Promise<
    | {
        id: string;
        userId: string;
        mediaUrl: string;
        timestamp: any;
        interval: number;
        likes: number;
        status: string;
        reactions?: {
          creative: number;
          brave: number;
          goodVibes: number;
          total: number;
        };
      }
    | null
  > {
    const user = await this.ensureAuth();
    const feedDocId = `${user.uid}_${interval}`;
    const subRef = doc(this.firestore, `challengesSequence/${interval}/submissions/${feedDocId}`);
    const snap = await getDoc(subRef);
    if (!snap.exists()) return null;
    const data = snap.data() as any;
    const reactions = data?.reactions ?? {
      creative: 0,
      brave: 0,
      goodVibes: 0,
      total: 0,
    };
    return {
      id: snap.id,
      ...data,
      likes: data?.likes ?? reactions.total ?? 0,
      status: (data?.status ?? 'approved') as string,
      reactions,
    };
  }

  async isExploreUnlockedForInterval(interval: number): Promise<boolean> {
    const user = await this.ensureAuth();
    if (user.uid === ChallengeService.ADMIN_UID) return true;

    const state = await this.ensureUserState(user.uid, true);
    const completed = state.completedIntervals || [];
    if (completed.includes(interval)) return true;
    return await this.hasAnySubmissionFor(user.uid, interval);
  }

  async getExploreFeed(): Promise<any[]> {
    const user = await this.ensureAuth();
    const state = await this.ensureUserState(user.uid, true);

    const completed: number[] = Array.isArray(state.completedIntervals) ? state.completedIntervals : [];

    const baseFirstIncomplete = this.getFirstIncompleteDay(completed);
    const baseAllDone = Array.from({ length: this.TOTAL_SEQUENCE_DAYS }, (_, i) => i + 1).every(
      (d) => completed.includes(d)
    );

    const saved = this.getActiveChallengeId();

    let current: number;

    if (!baseAllDone) {
      // ✅ Si estás en camino base, en recarga te quedas en base (y SOLO base)
      if (saved && saved >= 1 && saved <= this.TOTAL_SEQUENCE_DAYS) current = saved;
      else current = baseFirstIncomplete;
    } else if (state.selectedPath) {
      // ✅ Si base terminado y hay camino, respetar camino en recarga
      const start = this.getPathStart(state.selectedPath);
      const end = start + (this.PATH_LENGTH - 1);

      if (saved && saved >= start && saved <= end) current = saved;
      else current = this.getFirstIncompletePathInterval(completed, state.selectedPath);
    } else {
      // Base terminado y aún no eligió camino => mantener último base (selector)
      if (saved && saved >= 1 && saved <= this.TOTAL_SEQUENCE_DAYS) current = saved;
      else current = this.TOTAL_SEQUENCE_DAYS;
    }

    this.setActiveChallengeId(current);

    return await this.getFeedForInterval(current);
  }

  // ========= 🔹 IMPULSOS / REACCIONES =========

  private normalizeDocId(id: string): string {
    const s = String(id ?? '').trim();
    if (!s) return '';
    const parts = s.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : s;
  }

  /**
   * ✅ FIX DEFINITIVO:
   * - Transacción: leemos el reaction actual REAL en Firestore y calculamos deltas correctos.
   * - Serialización por (userUid + submissionId): evita carreras por clicks rápidos.
   * - Actualiza challengesSequence dentro de la TX.
   * - Actualiza userSubmissions fuera (best-effort) para evitar fallos por permisos/existencia.
   * - Mantiene 1 reacción por usuario por submission.
   */
  async toggleReaction(
    interval: number,
    submissionId: string,
    submissionOwnerUid: string,
    userUid: string,
    newType: ReactionType | null,
    previousType: ReactionType | null
  ): Promise<void> {
    const canonicalSubmissionId = this.normalizeDocId(submissionId);
    if (!canonicalSubmissionId) throw new Error('submissionId inválido');

    const key = `${userUid}_${canonicalSubmissionId}`;

    const prev = this.reactionQueue.get(key) ?? Promise.resolve();

    const next = prev
      .catch(() => {})
      .then(() =>
        this._toggleReactionTxn(
          interval,
          canonicalSubmissionId,
          submissionOwnerUid,
          userUid,
          newType,
          previousType
        )
      );

    this.reactionQueue.set(key, next);

    try {
      await next;
    } finally {
      if (this.reactionQueue.get(key) === next) {
        this.reactionQueue.delete(key);
      }
    }
  }

  private async _toggleReactionTxn(
    interval: number,
    canonicalSubmissionId: string,
    submissionOwnerUid: string,
    userUid: string,
    newType: ReactionType | null,
    previousType: ReactionType | null
  ): Promise<void> {
    const submissionSeqRef = doc(
      this.firestore,
      `challengesSequence/${interval}/submissions/${canonicalSubmissionId}`
    );

    // ✅ Fuente para el espejo global (best-effort fuera de TX)
    const submissionFlatRef = doc(this.firestore, `userSubmissions/${canonicalSubmissionId}`);

    // ✅ 1 doc por usuario (fuente de verdad)
    const reactionRef = doc(
      this.firestore,
      `challengesSequence/${interval}/submissions/${canonicalSubmissionId}/reactions/${userUid}`
    );

    const ownerStatsRef = submissionOwnerUid
      ? doc(this.firestore, `userProgress/${submissionOwnerUid}`)
      : null;

    const giverStatsRef = userUid ? doc(this.firestore, `userProgress/${userUid}`) : null;

    // ✅ Guardamos updateFlat fuera
    let updateFlat: any = {};
    let beforeTypeOutside: ReactionType | null = null;
    let afterTypeOutside: ReactionType | null = null;
    let impulsesDeltaOutside = 0;

    const res = await this.inCtx(() =>
      runTransaction(this.firestore, async (tx) => {
        const [seqSnap, reactionSnap] = await Promise.all([
          tx.get(submissionSeqRef),
          tx.get(reactionRef),
        ]);

        if (!seqSnap.exists()) {
          throw new Error('No se pudo actualizar la reacción (submission no encontrada).');
        }

        const currentType: ReactionType | null = reactionSnap.exists()
          ? ((reactionSnap.data() as any)?.type as ReactionType) ?? null
          : null;

        // Semántica:
        // - click mismo tipo => toggle off
        // - click tipo distinto => switch
        // - newType null => quitar
        let finalType: ReactionType | null = null;

        if (!newType) {
          finalType = null;
        } else if (currentType === newType) {
          finalType = null;
        } else {
          finalType = newType;
        }

        const updateSeq: any = {};
        updateFlat = {}; // ✅ se calcula aquí pero se aplicará fuera de TX

        const dec = (field: ReactionType) => {
          updateSeq[`reactions.${field}`] = increment(-1);
          updateFlat[`reactions.${field}`] = increment(-1);
        };

        const inc = (field: ReactionType) => {
          updateSeq[`reactions.${field}`] = increment(1);
          updateFlat[`reactions.${field}`] = increment(1);
        };

        if (currentType && currentType !== finalType) dec(currentType);
        if (finalType) inc(finalType);

        let deltaTotal = 0;
        if (!currentType && finalType) deltaTotal = 1;
        else if (currentType && !finalType) deltaTotal = -1;

        if (deltaTotal !== 0) {
          updateSeq['reactions.total'] = increment(deltaTotal);
          updateSeq['likes'] = increment(deltaTotal);

          updateFlat['reactions.total'] = increment(deltaTotal);
          updateFlat['likes'] = increment(deltaTotal);
        }

        if (Object.keys(updateSeq).length > 0) {
          tx.update(submissionSeqRef, updateSeq);
        }

        // Reaction doc (1 por usuario)
        if (!finalType) {
          if (reactionSnap.exists()) tx.delete(reactionRef);
        } else {
          tx.set(
            reactionRef,
            {
              type: finalType,
              userId: userUid,
              ownerId: submissionOwnerUid,
              interval,
              submissionId: canonicalSubmissionId,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        // impulsesReceived/Given SOLO cambian si se añade o se quita (no en switch)
        const impulsesDelta = deltaTotal;

        if (impulsesDelta !== 0) {
          if (ownerStatsRef) {
            tx.set(
              ownerStatsRef,
              { impulsesReceived: increment(impulsesDelta), updatedAt: serverTimestamp() },
              { merge: true }
            );
          }
          if (giverStatsRef) {
            tx.set(
              giverStatsRef,
              { impulsesGiven: increment(impulsesDelta), updatedAt: serverTimestamp() },
              { merge: true }
            );
          }
        }

        // Pasamos info para fuera
        beforeTypeOutside = currentType;
        afterTypeOutside = finalType;
        impulsesDeltaOutside = impulsesDelta;

        return {
          beforeType: currentType,
          afterType: finalType,
          impulsesDelta,
        };
      })
    );

    // ✅ Best-effort: actualizar userSubmissions fuera de TX (no rompe reacción)
    try {
      if (updateFlat && Object.keys(updateFlat).length > 0) {
        await updateDoc(submissionFlatRef, updateFlat);
      }
    } catch (e) {
      console.warn('⚠️ toggleReaction: no se pudo actualizar userSubmissions (best-effort):', e);
    }

    // ✅ Analytics: impulso dado / quitado / cambiado (estado real)
    const beforeType = ((res as any)?.beforeType as ReactionType | null) ?? beforeTypeOutside;
    const afterType = ((res as any)?.afterType as ReactionType | null) ?? afterTypeOutside;
    const impulsesDelta = Number((res as any)?.impulsesDelta ?? impulsesDeltaOutside ?? 0);

    let action: 'add' | 'remove' | 'switch' | 'noop' = 'noop';
    if (!beforeType && afterType) action = 'add';
    else if (beforeType && !afterType) action = 'remove';
    else if (beforeType && afterType && beforeType !== afterType) action = 'switch';

    if (action !== 'noop') {
      // ✅ Emitir evento para que Explore sincronice cache/UI al volver desde Feed
      try {
        const deltaTotal = !beforeType && afterType ? 1 : beforeType && !afterType ? -1 : 0;
        this.reactionChanged$.next({
          interval,
          submissionId: canonicalSubmissionId,
          userUid,
          ownerUid: submissionOwnerUid || null,
          beforeType: beforeType ?? null,
          afterType: afterType ?? null,
          deltaTotal,
          action,
        });
      } catch {
        // noop
      }

      this.logAnalytics('reaction_given', {
        interval,
        submission_id: canonicalSubmissionId,
        owner_uid: submissionOwnerUid || undefined,
        action,
        new_type: afterType || undefined,
        previous_type: beforeType || previousType || undefined,
      });
    }

    // ✅ Insignias por likes (recibidos / dados): 20, 50, 100 (MVP)
    // Solo si add/remove (switch no cambia totales)
    if (impulsesDelta !== 0) {
      try {
        // 1) Recibidos (dueño)
        if (submissionOwnerUid) {
          const ownerSnap = await getDoc(doc(this.firestore, `userProgress/${submissionOwnerUid}`));
          const ownerData: any = ownerSnap.exists() ? ownerSnap.data() : null;
          const receivedTotal = ownerData?.impulsesReceived ?? 0;
          await this.badgesService.checkLikeUnlocks(submissionOwnerUid, receivedTotal, 0);
        }

        // 2) Dados (el que reacciona)
        if (userUid) {
          const giverSnap = await getDoc(doc(this.firestore, `userProgress/${userUid}`));
          const giverData: any = giverSnap.exists() ? giverSnap.data() : null;
          const givenTotal = giverData?.impulsesGiven ?? 0;
          await this.badgesService.checkLikeUnlocks(userUid, 0, givenTotal);
        }
      } catch (e) {
        console.warn('⚠️ checkLikeUnlocks error (ignorado):', e);
      }
    }
  }

  async getUserReactionForSubmission(
    interval: number,
    submissionId: string,
    userUid: string
  ): Promise<ReactionType | null> {
    const canonicalSubmissionId = this.normalizeDocId(submissionId);
    if (!canonicalSubmissionId) return null;

    const reactionRef = doc(
      this.firestore,
      `challengesSequence/${interval}/submissions/${canonicalSubmissionId}/reactions/${userUid}`
    );
    const snap = await getDoc(reactionRef);
    if (!snap.exists()) return null;
    const data = snap.data() as any;
    return (data?.type ?? null) as ReactionType | null;
  }

  // ========= REPORTES =========

  /**
   * ✅ Robustez extra:
   * - Primero intenta leer challengesSequence/{interval}/submissions/{submissionId}
   * - Si no existe, fallback a userSubmissions/{submissionId}
   */
  async reportSubmission(
    interval: number,
    submissionId: string,
    reason: string = 'user_report'
  ): Promise<void> {
    const user = await this.ensureAuth();
    const reporterUid = user.uid;

    try {
      const submissionRef = doc(
        this.firestore,
        `challengesSequence/${interval}/submissions/${submissionId}`
      );
      let submissionSnap = await getDoc(submissionRef);

      let subData: any = null;

      if (submissionSnap.exists()) {
        subData = submissionSnap.data() as any;
      } else {
        const flatRef = doc(this.firestore, `userSubmissions/${submissionId}`);
        const flatSnap = await getDoc(flatRef);
        if (!flatSnap.exists()) {
          throw new Error('La participación ya no existe.');
        }
        subData = flatSnap.data() as any;
      }

      const ownerId: string | null = subData?.userId || subData?.uid || null;

      const reportId = `${interval}_${submissionId}_${reporterUid}`;
      const reportRef = doc(this.firestore, `reports/${reportId}`);

      await setDoc(
        reportRef,
        {
          submissionId,
          interval,
          ownerId,
          reportedBy: reporterUid,
          reason: reason || 'user_report',
          status: 'pending',
          createdAt: serverTimestamp(),
          lastUpdateAt: serverTimestamp(),
        },
        { merge: true }
      );

      try {
        const hiddenId = `${interval}_${submissionId}`;
        const hiddenRef = doc(
          this.firestore,
          `userHiddenSubmissions/${reporterUid}/items/${hiddenId}`
        );

        await setDoc(
          hiddenRef,
          {
            interval,
            submissionId,
            ownerId,
            reportId,
            status: 'pending',
            createdAt: serverTimestamp(),
            lastUpdateAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn(
          '⚠️ No se pudo escribir userHiddenSubmissions, pero el report global SÍ se creó:',
          e
        );
      }
    } catch (err) {
      console.error('❌ Error en reportSubmission:', err);
      throw err;
    }
  }

  async isSubmissionHiddenForUser(
    interval: number,
    submissionId: string,
    userUid: string
  ): Promise<boolean> {
    if (!userUid) return false;
    try {
      const hiddenId = `${interval}_${submissionId}`;
      const hiddenRef = doc(this.firestore, `userHiddenSubmissions/${userUid}/items/${hiddenId}`);
      const snap = await getDoc(hiddenRef);
      if (!snap.exists()) return false;
      const data = snap.data() as any;
      return (data?.status ?? 'pending') === 'pending';
    } catch (e) {
      console.warn('⚠️ isSubmissionHiddenForUser error:', e);
      return false;
    }
  }

  async adminGetOpenReports(): Promise<
    Array<{
      id: string;
      submissionId: string;
      interval: number;
      ownerId: string | null;
      reporterUid: string;
      reason: string;
      status: string;
      createdAt: any;
    }>
  > {
    const user = this.auth.currentUser;
    if (!user || user.uid !== ChallengeService.ADMIN_UID) {
      throw new Error('Solo el admin puede leer reports.');
    }

    const reportsRef = collection(this.firestore, 'reports');
    const qReports = query(reportsRef, where('status', '==', 'pending'));
    const snap = await this.inCtx(() => getDocs(qReports));

    return snap.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: d.id,
        submissionId: data.submissionId,
        interval: data.interval,
        ownerId: data.ownerId ?? null,
        reporterUid: data.reportedBy ?? data.reporterUid ?? '',
        reason: data.reason ?? 'user_report',
        status: data.status ?? 'pending',
        createdAt: data.createdAt ?? null,
      };
    });
  }

  // ========= OBSERVABLES =========

  observeUserStats(): Observable<{ coins: number; streak: number; impulsesReceived: number }> {
    return new Observable((subscriber) => {
      this.ensureAuth()
        .then((user) => {
          const ref = doc(this.firestore, `userProgress/${user.uid}`);
          const sub = docData(ref).subscribe({
            next: (d: any) =>
              subscriber.next({
                coins: d?.coins ?? 0,
                streak: d?.streak ?? 0,
                impulsesReceived: d?.impulsesReceived ?? 0,
              }),
            error: (e) => subscriber.error(e),
          });
          return () => sub.unsubscribe();
        })
        .catch((err) => subscriber.error(err));
    });
  }

  // ========== Ventanas / ANCLA (LEGACY) ==========

  async getWindowInfo(
    uid: string
  ): Promise<{
    currentDay: number;
    nextCutoverMs: number;
    nowMs: number;
    ended: boolean;
  }> {
    const state = await this.ensureUserState(uid, true);
    const now = this.getSpainNow().getTime();
    const currentDay = state.currentDay || 1;
    return {
      currentDay,
      nextCutoverMs: now,
      nowMs: now,
      ended: false,
    };
  }

  async syncDayFromAnchor(uid: string): Promise<{ currentDay: number; nextCutoverMs: number }> {
    const { currentDay, nextCutoverMs } = await this.getWindowInfo(uid);
    this.setActiveChallengeId(currentDay);
    return { currentDay, nextCutoverMs };
  }

  async checkAndResetStreakIfMissed(uid: string): Promise<void> {
    try {
      const state = await this.ensureUserState(uid, true);
      this.setActiveChallengeId(state.currentDay || 1);
    } catch (err) {
      console.error('checkAndResetStreakIfMissed error:', err);
    }
  }

  // ========= 🔹 obtener datos de usuario para enriquecer el feed =========
  async getUserInfo(uid: string): Promise<{ displayName?: string; avatar?: string } | null> {
    try {
      const ref = doc(this.firestore, 'users', uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;

      const d = snap.data() as any;

      console.log('[ChallengeService.getUserInfo] uid =', uid, 'data =', d);

      return {
        displayName: d?.displayName ?? d?.userName ?? d?.name ?? d?.nombre ?? undefined,
        avatar: d?.avatar ?? d?.photoURL ?? d?.PhotoURL ?? d?.photoUrl ?? d?.photo ?? undefined,
      };
    } catch (err) {
      console.error('⚠️ Error obteniendo usuario:', err);
      return null;
    }
  }

  // ========= 🔔 anunciar cambio de reto para push 1-a-1 =========
  private async announceUserChallengeChangeFor(uid: string, day: number): Promise<void> {
    try {
      const ch = await this.getChallengeByDay(day);
      await this.inCtx(() =>
        setDoc(
          doc(this.firestore, `users/${uid}/devCurrent`),
          {
            activeChallengeId: String(day),
            title: ch?.title ?? null,
            changedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );
    } catch (err) {
      console.error('⚠️ announceUserChallengeChangeFor error:', err);
    }
  }

  public async __debugForceDevCurrent(uid: string, day: number): Promise<void> {
    try {
      const ch = await this.getChallengeByDay(day);
      await setDoc(
        doc(this.firestore, `users/${uid}/devCurrent`),
        {
          activeChallengeId: String(day),
          title: ch?.title ?? `Reto ${day}`,
          changedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log('[debug] devCurrent escrito para', uid, 'day', day);
    } catch (e) {
      console.error('[debug] devCurrent error', e);
    }
  }

  // ========= creación 1 vez de la badge Pionero =========
  private async awardPioneroIfFirstTime(uid: string): Promise<boolean> {
    try {
      const badgeRef = doc(this.firestore, `userBadges/${uid}/earned/pionero`);
      const snap = await getDoc(badgeRef);
      if (snap.exists()) return false;

      await setDoc(
        badgeRef,
        {
          id: 'pionero',
          unlocked: true,
          createdAt: Date.now(),
          source: 'first_submission',
        },
        { merge: true }
      );

      return true;
    } catch (err) {
      console.error('awardPioneroIfFirstTime error:', err);
      return false;
    }
  }
}
